// ----------------------------------------------------------------------------
// AiProposalSection
// ----------------------------------------------------------------------------
// Client-side "AI Bid Assistant" inside the drawer. Takes user company info,
// runs a simulated 3-second generation cycle with rotating status copy, and
// renders a structured proposal that merges the form inputs with the bid.
//
// In production: replace the `setTimeout`-driven mock with a real call to a
// `/api/proposals/generate` endpoint (Claude/GPT-backed). The local proposal
// string is bubbled up via `onProposalChange` so the parent can include it in
// the bid-package download.
// ----------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Cpu, Loader2, Copy, Check, Eraser, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { AI, SUBMIT } from "../constants/testIds";
import { SubmissionModal } from "./SubmissionModal";

const LOADING_STAGES = [
  "Analyzing VendorLink / Bonfire requirements…",
  "Matching company qualifications…",
  "Drafting formal executive summary…",
];

// Build the structured proposal string. Kept pure so it can be re-used by the
// download function in the parent component.
export const buildProposal = ({ bid, company, amount, notes }) => {
  const amt = Number(amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
  });
  const qualifications =
    notes?.trim() ||
    "established commercial operator with documented municipal performance history";
  return `EXECUTIVE PROPOSAL FOR: ${bid.title}
SUBMITTED BY: ${company || "[Company Name]"}
TOTAL PROPOSED COST: $${amt}
SOLICITATION REFERENCE: ${bid.id} · ${bid.agency} · ${bid.platform}

────────────────────────────────────────────────────────────────
SECTION 1 · EXECUTIVE SUMMARY
────────────────────────────────────────────────────────────────
${company || "Our firm"} respectfully submits this proposal in response to
${bid.agency}'s solicitation ${bid.id} — "${bid.title}". As a ${qualifications},
we are uniquely positioned to deliver the technical scope outlined in the
${bid.type} package while honoring the operational and procurement standards
expected of vendors on the ${bid.platform} platform. Our pricing of
$${amt} reflects a fully-burdened, all-inclusive engagement with no
post-award change-order assumptions baked into the base bid.

────────────────────────────────────────────────────────────────
SECTION 2 · OPERATIONAL TIMELINE & COMPLIANCE
────────────────────────────────────────────────────────────────
Field operations will adhere strictly to Brevard County safety protocols,
OSHA 1926 construction standards, and the published scheduling parameters
for ${bid.location}. A dedicated project superintendent will be assigned on
Day 1 with a documented two-week look-ahead, daily JHA briefings, and a
weekly progress report to the agency's contract administrator. All bonding,
insurance, and minority-participation reporting will be executed in lockstep
with the agency's award-stage compliance checklist. Subcontractors will be
pre-qualified through our standing vendor-management program prior to any
on-site mobilization, and all close-out documentation will be delivered
electronically within ten (10) business days of substantial completion.

Respectfully submitted,
${company || "[Company Name]"}
`;
};

export const AiProposalSection = ({ bid, proposal, onProposalChange }) => {
  // Per-bid form state — resets when bid.id changes (parent unmounts drawer).
  const [company, setCompany] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  // Generation loop state.
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const [copied, setCopied] = useState(false);
  // Submission modal visibility — opens when user clicks "Submit Proposal to Agency".
  const [submitOpen, setSubmitOpen] = useState(false);
  const timers = useRef([]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => timers.current.forEach(clearTimeout);
  }, []);

  const startGeneration = () => {
    if (!company.trim() || !amount) {
      toast.error("Add a company name and bid amount first");
      return;
    }
    setLoading(true);
    setStage(0);
    timers.current.forEach(clearTimeout);
    timers.current = [
      setTimeout(() => setStage(1), 1000),
      setTimeout(() => setStage(2), 2000),
      setTimeout(() => {
        const text = buildProposal({ bid, company, amount, notes });
        onProposalChange(text);
        setLoading(false);
        toast.success("Proposal generated");
      }, 3000),
    ];
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(proposal);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const handleClear = () => {
    onProposalChange("");
    toast.message("Proposal cleared");
  };

  return (
    <section className="border-t border-slate-200 bg-slate-50/40">
      {/* Tab-style header */}
      <div className="px-6 pt-5 pb-3 flex items-center gap-2">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700">
          <Sparkles size={12} strokeWidth={2} />
          <span className="text-[10px] font-bold uppercase tracking-[0.15em]">
            AI Bid Assistant
          </span>
        </div>
        <span className="text-[11px] text-slate-500">
          Draft a tailored proposal in seconds.
        </span>
      </div>

      <div className="px-6 pb-6 space-y-4">
        {/* Form */}
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
              Company Name
            </label>
            <Input
              data-testid={AI.inputCompany}
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="e.g., Space Coast Commercial Services"
              className="h-10 bg-white border-slate-200"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
              Your Bid Amount ($)
            </label>
            <Input
              data-testid={AI.inputAmount}
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g., 45000"
              className="h-10 bg-white border-slate-200 font-mono"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
              Key Qualifications & Scope Notes
            </label>
            <Textarea
              data-testid={AI.inputNotes}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., 15 years local experience, certified green materials, 10-man active crew, can finish in 30 days"
              className="min-h-[88px] bg-white border-slate-200 resize-none"
              disabled={loading}
            />
          </div>
        </div>

        {/* Generate button */}
        <button
          type="button"
          data-testid={AI.generate}
          onClick={startGeneration}
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm shadow-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 size={16} strokeWidth={2} className="animate-spin" />
              Generating…
            </>
          ) : (
            <>
              <Cpu size={16} strokeWidth={1.9} />
              Generate AI Proposal
            </>
          )}
        </button>

        {/* Loading status */}
        {loading && (
          <div
            data-testid={AI.spinner}
            className="rounded-md border border-indigo-100 bg-indigo-50/60 p-4 flex items-center gap-3 animate-fade-in"
          >
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-indigo-600" />
            </span>
            <p className="text-xs text-indigo-900 font-medium">
              {LOADING_STAGES[stage]}
            </p>
          </div>
        )}

        {/* Output */}
        {!loading && proposal && (
          <div
            data-testid={AI.output}
            className="rounded-md border border-slate-200 bg-white overflow-hidden animate-fade-in"
          >
            <div className="flex items-center justify-between px-3.5 py-2 border-b border-slate-100 bg-slate-50/60">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
                <Sparkles size={12} strokeWidth={2} className="text-indigo-600" />
                Generated Proposal
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid={AI.copy}
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check size={11} strokeWidth={2} /> Copied
                    </>
                  ) : (
                    <>
                      <Copy size={11} strokeWidth={1.9} /> Copy
                    </>
                  )}
                </button>
                <button
                  type="button"
                  data-testid={AI.clear}
                  onClick={handleClear}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <Eraser size={11} strokeWidth={1.9} /> Clear
                </button>
              </div>
            </div>
            <pre className="font-mono text-[11.5px] leading-relaxed text-slate-700 px-4 py-4 max-h-72 overflow-y-auto thin-scroll whitespace-pre-wrap">
              {proposal}
            </pre>
            <div className="px-4 py-2 border-t border-slate-100 bg-indigo-50/40 text-[10.5px] text-indigo-900/80">
              ✦ This proposal is now bundled with the Download Full Bid Package
              export.
            </div>
          </div>
        )}

        {/* Submit to agency — appears directly beneath the generated proposal */}
        {!loading && proposal && (
          <button
            type="button"
            data-testid={SUBMIT.open}
            onClick={() => setSubmitOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm transition-colors animate-fade-in"
          >
            <Send size={16} strokeWidth={1.9} />
            Submit Proposal to Agency
          </button>
        )}
      </div>

      {/* Secure submission overlay */}
      <SubmissionModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        bid={bid}
        company={company}
        amount={amount}
      />
    </section>
  );
};
